import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../configureStore';

export interface SiloFillLevel {
    siloId: string;
    siloName: string;
    maxCapacityTons: number | null;
    currentTons: number | null;
    currentPercent: number | null;
    lastUpdate: string | null;
    hasSensorData?: boolean;
}

export interface SiloHistory {
    siloName: string;
    fillLevels: FillLevel[];
}

export interface FillLevel {
    timestamp: string;
    currentTons: number | null;
    currentPercent: number | null;
}

export interface SiloDeliveryRow {
    periodStart: string;
    periodEnd: string;
    articleName: string;
    articleNumber: string;
    deliveriesCount: number;
}

export interface SiloDetailsDto {
    siloName: string;
    customerNumber: string;
    capacityTons: number | null;
    volumeM3: number | null;
    hasSensorData?: boolean;
    currentPercent?: number | null;
    lastUpdate?: string | null;
    consumptionEnabled?: boolean | null;
    dailyConsumptionKg?: number | null;
    warningsEnabled?: boolean | null;
    thresholdPercent?: number | null;
}

export interface SiloState {
    hasSilos: boolean;
    fillLevels: SiloFillLevel[];
    siloHistory: SiloHistory | null;
    siloDeliveries: SiloDeliveryRow[];
    isLoading: boolean;
    error: string | null;
    isCreating?: boolean;
    createError?: string | null;
    currentSiloDetails?: SiloDetailsDto | null;
    isDetailsLoading?: boolean;
    detailsError?: string | null;
    isUpdating?: boolean;
    updateError?: string | null;
    isDeleting?: boolean;
    deleteError?: string | null;
}

export const INITIAL_SILO_STATE: SiloState = {
    hasSilos: false,
    fillLevels: [],
    siloHistory: null,
    siloDeliveries: [],
    isLoading: false,
    error: null,
    isCreating: false,
    createError: null,
    currentSiloDetails: null,
    isDetailsLoading: false,
    detailsError: null,
    isUpdating: false,
    updateError: null,
    isDeleting: false,
    deleteError: null,
};

const siloSlice = createSlice({
    name: 'silo',
    initialState: INITIAL_SILO_STATE,
    reducers: {
        createSilo: (state, _action: PayloadAction<CreateSiloActionPayload>) => {
            state.isCreating = true;
            state.createError = null;
        },
        createSiloSucceeded: (state) => {
            state.isCreating = false;
            state.createError = null;
        },
        createSiloFailed: (state, action: PayloadAction<string | undefined>) => {
            state.isCreating = false;
            state.createError = action.payload ?? 'Silo konnte nicht erstellt werden';
        },
        checkSilos: (_state) => {
            // Saga will handle this
        },
        checkSilosSucceeded: (state, action: PayloadAction<boolean>) => {
            state.hasSilos = action.payload;
        },
        checkSilosFailed: (state) => {
            state.hasSilos = false;
        },
        fetchSiloFillLevels: (state) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchSiloFillLevelsSucceeded: (state, action: PayloadAction<SiloFillLevel[]>) => {
            state.fillLevels = action.payload;
            state.isLoading = false;
            state.error = null;
        },
        fetchSiloFillLevelsFailed: (state) => {
            state.isLoading = false;
            state.error = 'Failed to fetch silo fill levels';
        },
        fetchSiloHistory: (state, _action: PayloadAction<string | null>) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchSiloHistorySucceeded: (state, action: PayloadAction<SiloHistory>) => {
            state.siloHistory = action.payload;
            state.isLoading = false;
            state.error = null;
        },
        fetchSiloHistoryFailed: (state) => {
            state.isLoading = false;
            state.error = 'Failed to fetch silo details';
        },
        fetchSiloDeliveries: (state) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchSiloDeliveriesSucceeded: (state, action: PayloadAction<SiloDeliveryRow[]>) => {
            state.siloDeliveries = action.payload;
            state.isLoading = false;
            state.error = null;
        },
        fetchSiloDeliveriesFailed: (state) => {
            state.isLoading = false;
            state.error = 'Failed to fetch silo deliveries';
        },
        fetchSiloDetails: (state, _action: PayloadAction<string>) => {
            state.isDetailsLoading = true;
            state.detailsError = null;
        },
        fetchSiloDetailsSucceeded: (state, action: PayloadAction<SiloDetailsDto>) => {
            state.currentSiloDetails = action.payload;
            state.isDetailsLoading = false;
            state.detailsError = null;
        },
        fetchSiloDetailsFailed: (state) => {
            state.isDetailsLoading = false;
            state.detailsError = 'Failed to fetch silo details';
        },
        updateSilo: (state, _action: PayloadAction<UpdateSiloActionPayload>) => {
            state.isUpdating = true;
            state.updateError = null;
        },
        updateSiloSucceeded: (state, action: PayloadAction<SiloDetailsDto>) => {
            state.isUpdating = false;
            state.updateError = null;
            state.currentSiloDetails = action.payload;
        },
        updateSiloFailed: (state, action: PayloadAction<string | undefined>) => {
            state.isUpdating = false;
            state.updateError = action.payload ?? 'Silo konnte nicht aktualisiert werden';
        },
        deleteSilo: (state, _action: PayloadAction<string>) => {
            state.isDeleting = true;
            state.deleteError = null;
        },
        deleteSiloSucceeded: (state) => {
            state.isDeleting = false;
            state.deleteError = null;
        },
        deleteSiloFailed: (state, action: PayloadAction<string | undefined>) => {
            state.isDeleting = false;
            state.deleteError = action.payload ?? 'Silo konnte nicht gelöscht werden';
        },
    },
});

export const {
    createSilo,
    createSiloSucceeded,
    createSiloFailed,
    checkSilos,
    checkSilosSucceeded,
    checkSilosFailed,
    fetchSiloFillLevels,
    fetchSiloFillLevelsSucceeded,
    fetchSiloFillLevelsFailed,
    fetchSiloHistory,
    fetchSiloHistorySucceeded,
    fetchSiloHistoryFailed,
    fetchSiloDeliveries,
    fetchSiloDeliveriesSucceeded,
    fetchSiloDeliveriesFailed,
    fetchSiloDetails,
    fetchSiloDetailsSucceeded,
    fetchSiloDetailsFailed,
    updateSilo,
    updateSiloSucceeded,
    updateSiloFailed,
    deleteSilo,
    deleteSiloSucceeded,
    deleteSiloFailed,
} = siloSlice.actions;

export const selectHasSilos = (state: RootState): boolean => state.silo.hasSilos;

export const siloReducer = siloSlice.reducer;

export type CreateSiloPayload = {
    siloNumberOrName: string;
    customerNumber: string;
    capacityTons?: number;
    initialFillLevel?: number;
    volumeM3?: number;
    consumptionEnabled?: boolean;
    dailyConsumptionKg?: number;
    warningsEnabled?: boolean;
    thresholdPercent?: number;
};

export type CreateSiloActionPayload = CreateSiloPayload & { onSuccessNavigateToOverview?: boolean };

export type UpdateSiloActionPayload = {
    siloId: string;
    siloName: string;
    customerNumber: string;
    capacityTons?: number;
    initialFillTons?: number;
    volumeM3?: number;
    consumptionEnabled?: boolean;
    dailyConsumptionKg?: number;
    warningsEnabled?: boolean;
    thresholdPercent?: number;
    fillLevel?: {
        currentTons: number;
        timestamp: string;
    };
};
